"use client";

import { Accordion, AccordionItem, Typography } from "@repo/ui";
import LoadMore from "../LoadMore";
import StructuredData from "./StructuredData";
import useFaqParams from "@/hooks/useFaqParams";
import { useSuspenseQuery } from "@apollo/client";
import { FaqType, GET_FAQS } from "@/graphql/queries/faqs-query";
import { useMemo, forwardRef, useImperativeHandle } from "react";

type FaqsProps = {
  heading?: string;
};

export type FaqsRef = {
  refetch: (variables?: { faqType: FaqType }) => Promise<any>;
};

const Faqs = forwardRef<FaqsRef, FaqsProps>((props, ref) => {
  const { faqType, slug, country, city } = useFaqParams();

  const { data, error, refetch } = useSuspenseQuery(GET_FAQS, {
    variables: { faqType: faqType, slug: slug, country: country, city: city },
  });

  useImperativeHandle(ref, () => ({
    refetch: (variables) => refetch(variables),
  }));

  const faqSchema = useMemo(() => {
    const questionAnswers = data.faqs.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    }));

    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: questionAnswers,
    };
  }, [data.faqs]);

  if (error) {
    console.error("Error fetching FAQs:", error);
    return <div>Error loading FAQs. Please try again later.</div>;
  }
  return (
    <>
      <LoadMore
        wrapper={<Accordion multiple={false} />}
        items={data?.faqs || []}
        render={(item, index) => (
          <AccordionItem key={`faq-${index}`} title={item.question}>
            <Typography>{item.answer}</Typography>
          </AccordionItem>
        )}
      />
      {/* Faq Json Schema */}
      {data?.faqs?.length > 0 && <StructuredData data={faqSchema} />}
    </>
  );
});

Faqs.displayName = "Faqs";

export default Faqs;
